import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';
import { Autocomplete, CircularProgress, FormControl, TextField } from '@mui/material';
import { ConfigEntryType } from 'config/config';
import useUserData from 'hooks/useUserData';
import { useEffect, KeyboardEvent, useState, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

type RetroFormProjectMilestoneProps = {
  field: ConfigEntryType;
};
type OptionType = { label: string; value: string };

function RetroFormProjectMilestone({ field }: RetroFormProjectMilestoneProps) {
  const {
    formState: { isSubmitting },
    setValue,
    control,
  } = useFormContext();
  const [open, setOpen] = useState(false);
  const value = useWatch({ control, name: field?.entryCode, defaultValue: '' });
  const phasesValue = useWatch({ control, name: 'projectPhases' });
  const selectedPhases = useMemo(
    () => (Array.isArray(phasesValue) ? phasesValue : phasesValue ? [phasesValue] : []) as string[],
    [phasesValue],
  );
  const { companyId } = useUserData();
  const { isLoading, activeUniqueEntries, isFetched } = useConfigQuery({
    companyId,
    configName: CONFIGS.RETROSPECTIVE_PHASES.configName,
  });

  const milestoneOptions = useMemo(() => {
    if (selectedPhases.length) {
      const filteredEntry = activeUniqueEntries.find((entry) =>
        selectedPhases.includes(entry.entryCode),
      );
      if (filteredEntry) {
        return filteredEntry.entryValues.map((value) => ({
          label: value,
          value: value,
        }));
      }
    }
    return [];
  }, [selectedPhases, activeUniqueEntries]);

  const handleSelect = (_: unknown, values: OptionType | null | string) => {
    const selectedValue = typeof values === 'string' ? values : values?.value;
    setValue(field.entryCode, selectedValue || '', { shouldDirty: true, shouldValidate: true });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      setOpen(true);
    }
  };

  useEffect(() => {
    // Check if the current milestone is still a valid option
    const matchingOption = milestoneOptions?.find((option) => option?.value === value);

    if (isFetched && !matchingOption && value) {
      setValue('projectMileStones', null);
    }
  }, [milestoneOptions, value, isFetched]);

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth>
            <Autocomplete
              id={field.entryCode}
              {...(field?.referenceObject?.freeText && {
                freeSolo: true,
                open,
                onOpen: (event) => {
                  const currentValue = (event.target as HTMLInputElement).value;
                  if (currentValue?.trim()) {
                    setOpen(true);
                  }
                },
                onClose: () => setOpen(false),
                onKeyDown: handleKeyDown,
              })}
              onChange={handleSelect}
              options={milestoneOptions}
              autoHighlight
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              value={typeof value === 'string' ? { label: value, value } : value}
              noOptionsText={isLoading ? 'Loading...' : 'No Data Found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  placeholder={`${field.referenceObject?.placeHolder || 'Select'}`}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              disabled={isSubmitting || selectedPhases.length === 0}
            />
          </FormControl>
        )}
        name={field.entryCode}
      />
    </>
  );
}

export default RetroFormProjectMilestone;
